import React, { useState, useEffect } from 'react';
import { ideaApi } from '../services/api';
import toast from 'react-hot-toast';
import './ActionItems.css';

export interface ActionItem {
  id: number;
  ideaId: number;
  text: string;
  type: 'todo' | 'reminder' | 'follow_up' | 'research';
  priority?: 'high' | 'medium' | 'low';
  dueDate?: string;
  completed: boolean;
  extractedFrom: string;
  createdAt?: string;
}

interface ActionItemsProps {
  filter?: 'all' | 'today' | 'week' | 'overdue';
}

export const ActionItems: React.FC<ActionItemsProps> = ({ filter = 'all' }) => {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    fetchActionItems();
  }, []);

  const fetchActionItems = async () => {
    setIsLoading(true);
    try {
      const items = await ideaApi.getActionItems();
      setActionItems(items);
    } catch (error) {
      console.error('Failed to fetch action items:', error);
      toast.error('Failed to load action items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleComplete = async (item: ActionItem) => {
    try {
      await ideaApi.updateActionItem(item.id, { completed: !item.completed });
      setActionItems(prev => 
        prev.map(a => a.id === item.id ? { ...a, completed: !a.completed } : a)
      );
      toast.success(item.completed ? 'Marked as incomplete' : 'Marked as complete');
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'todo': return '✓';
      case 'reminder': return '⏰';
      case 'follow_up': return '📞';
      case 'research': return '🔍';
      default: return '📌';
    }
  };

  const getPriorityClass = (priority?: string) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const formatDueDate = (dateString?: string) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return <span className="overdue">Overdue</span>;
    if (diffDays === 0) return <span className="due-today">Due today</span>;
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays < 7) return `Due in ${diffDays} days`;
    
    return date.toLocaleDateString();
  };

  const filterItems = (items: ActionItem[]) => {
    let filtered = items;
    
    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(item => item.type === selectedType);
    }
    
    // Date filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    switch (filter) {
      case 'today':
        filtered = filtered.filter(item => {
          if (!item.dueDate) return false;
          const dueDate = new Date(item.dueDate);
          return dueDate <= new Date(today.getTime() + 24 * 60 * 60 * 1000);
        });
        break;
      case 'week':
        filtered = filtered.filter(item => {
          if (!item.dueDate) return false;
          const dueDate = new Date(item.dueDate);
          return dueDate <= weekFromNow;
        });
        break;
      case 'overdue':
        filtered = filtered.filter(item => {
          if (!item.dueDate) return false;
          return new Date(item.dueDate) < now;
        });
        break;
    }
    
    return filtered;
  };

  const filteredItems = filterItems(actionItems);
  const incompleteItems = filteredItems.filter(item => !item.completed);
  const completedItems = filteredItems.filter(item => item.completed);

  if (isLoading) {
    return <div className="action-items-loading">Loading action items...</div>;
  }

  return (
    <div className="action-items">
      <div className="action-items-header">
        <h3>Action Items ({incompleteItems.length})</h3>
        <div className="type-filters">
          <button 
            className={selectedType === 'all' ? 'active' : ''}
            onClick={() => setSelectedType('all')}
          >
            All
          </button>
          <button 
            className={selectedType === 'todo' ? 'active' : ''}
            onClick={() => setSelectedType('todo')}
          >
            ✓ Tasks
          </button>
          <button 
            className={selectedType === 'reminder' ? 'active' : ''}
            onClick={() => setSelectedType('reminder')}
          >
            ⏰ Reminders
          </button>
          <button 
            className={selectedType === 'follow_up' ? 'active' : ''}
            onClick={() => setSelectedType('follow_up')}
          >
            📞 Follow-ups
          </button>
          <button 
            className={selectedType === 'research' ? 'active' : ''}
            onClick={() => setSelectedType('research')}
          >
            🔍 Research
          </button>
        </div>
      </div>

      <div className="action-items-list">
        {incompleteItems.length === 0 ? (
          <div className="empty-state">
            <p>No action items found</p>
            <p className="hint">Action items are automatically extracted from your ideas</p>
          </div>
        ) : (
          incompleteItems.map(item => (
            <div 
              key={item.id} 
              className={`action-item ${getPriorityClass(item.priority)}`}
            >
              <input
                type="checkbox"
                checked={item.completed}
                onChange={() => handleToggleComplete(item)}
                className="action-checkbox"
              />
              <span className="action-type" title={item.type}>
                {getTypeIcon(item.type)}
              </span>
              <div className="action-content">
                <div className="action-text">{item.text}</div>
                <div className="action-meta">
                  <span className="extracted-from" title={item.extractedFrom}>
                    From: "{item.extractedFrom.substring(0, 50)}..."
                  </span>
                  {item.dueDate && (
                    <span className="due-date">{formatDueDate(item.dueDate)}</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {completedItems.length > 0 && (
        <div className="completed-section">
          <h4>Completed ({completedItems.length})</h4>
          <div className="completed-items">
            {completedItems.map(item => (
              <div key={item.id} className="action-item completed">
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => handleToggleComplete(item)}
                  className="action-checkbox"
                />
                <span className="action-type">{getTypeIcon(item.type)}</span>
                <div className="action-content">
                  <div className="action-text">{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};